import { reactive, toRaw } from 'vue';
import { v4 as uuid } from 'uuid';
import * as MSTeamsSDK from '@microsoft/teams-js';

// Modules
import { useAuth } from '@/modules/auth';
import { useExtensions } from '@/modules/user';
import { useServices } from '@/modules/service-manager';
import { useContext, VBCShellContext } from '@/modules/context';

// APIs
import { createSession, endSession } from '@/api/ms-teams';

// Common
import { PLATFORM, LOCAL_STORAGE } from '@/common/const';
import { MS_TEAMS_AUTH_STEPS } from '@/router/const';
import { getQueryParameters, toQueryString } from '@/common/utils';

class SessionCreationError extends Error {
  public readonly title: string = 'Oops, something went wrong';
  public readonly description: string = 'Please try signing in again.';
  constructor() {
    super('SessionCreationError');
  }
}

class AdminConsentError extends Error {
  public readonly title: string = 'Admin Consent Required';
  public readonly description: string;
  private readonly adminConsentLink: string;
  constructor(tenantId) {
    super('AdminConsentError');
    const state = uuid();
    this.adminConsentLink = `https://login.microsoftonline.com/${tenantId}/v2.0/adminconsent?client_id=${process.env.VUE_APP_MS_TEAMS_CLIENT_ID}&state=${state}&redirect_uri=${window.location.origin}${MS_TEAMS_AUTH_STEPS.ADMIN_CONSENT_CALLBACK}&scope=https://graph.microsoft.com/.default`;
    this.description = `Your Azure org admin will have to grant consent before you can start using VBC.<br>If you are an admin, click <a target="_blank" href="${this.adminConsentLink}">here</a> to grant consent<br> You will have to login again after consent is granted.`;
  }
}

const { setTokens, clearTokens, redirectToSSO, consumeSSOTokens, logout } = useAuth();
const { activeExtension } = useExtensions();

const { TEAMS_STATE_ID_KEY, SSO_FORCE_LOGIN_KEY } = LOCAL_STORAGE;
const fromCache = localStorage.getItem(TEAMS_STATE_ID_KEY);
const FAILED_TO_REMOVE_SESSION_LOGOUT_ERROR_REASON = 'Failed to remove session';
const TEAMS_HOST_CLIENT_TYPES = {
  web: 'Web',
  desktop: 'Native',
  ios: 'iOS',
  android: 'Android',
};

let initialState = {
  session: {
    id: '',
  },
};

if (fromCache) {
  initialState = JSON.parse(fromCache);
}

const state = reactive(initialState);
console.log(`MSTeams State loaded as ${JSON.stringify(toRaw(state))}`);

MSTeamsSDK.initialize();

function buildVBCContext(context, qs = { entityId: '' }): Partial<VBCShellContext> {
  let routeContext;

  try {
    routeContext = JSON.parse(context.subEntityId);
  } catch (e) {
    console.error(e);
    console.log('Failed parsing subEntityId from query string parameters');
  }

  return {
    appId: context.entityId || qs.entityId,
    platform: PLATFORM.MS_TEAMS,
    routeContext,
    hostClientType: TEAMS_HOST_CLIENT_TYPES[context.hostClientType] || context.hostClientType,
  };
}

function parseTeamsContext(): Promise<any> {
  const qs = getQueryParameters();
  console.log(`MSTeams queryString params - ${JSON.stringify(qs)}`);

  if (qs && qs.entityId && qs.subEntityId && qs.hostClientType) {
    console.log('Building context using query params');
    return Promise.resolve(buildVBCContext(qs));
  }

  return new Promise((resolve) => {
    MSTeamsSDK.getContext((context) => {
      console.log(`Building context using teams SDK ${JSON.stringify(context)}`);
      resolve(buildVBCContext(context, { entityId: qs.entityId }));
    });
  });
}

function redirectToUserConsentPage() {
  MSTeamsSDK.getContext((context) => {
    const queryParams = {
      client_id: process.env.VUE_APP_MS_TEAMS_CLIENT_ID,
      response_type: 'token',
      response_mode: 'fragment',
      prompt: 'consent',
      scope: 'https://graph.microsoft.com/User.Read',
      redirect_uri: `${window.location.origin}${MS_TEAMS_AUTH_STEPS.START}`,
      login_hint: context.loginHint || '',
      state: uuid(),
      nonce: uuid(),
    };

    const authorizeEndpoint = `https://login.microsoftonline.com/${context.tid}/oauth2/v2.0/authorize?${toQueryString(queryParams)}`;
    window.location.replace(authorizeEndpoint);
  });
}

async function openTeamsAuthenticationPopup(url, options = { width: 500, height: 600 }): Promise<Record<string, any>> {
  return await new Promise((resolve, reject) => {
    MSTeamsSDK.authentication.authenticate({
      url,
      width: options.width,
      height: options.height,
      successCallback(result) {
        // VBC access tokens were sent from the authentication popup into the current tab. we want to update the tokens in super.
        try {
          // Microsoft has a bug on Android devices, where "result" is not a string.
          if (typeof result === 'string') {
            resolve(JSON.parse(result));
          } else {
            resolve(result as any);
          }
        } catch (e) {
          reject(e.toString());
        }
      },
      failureCallback(reason) {
        console.error(reason);
        reject(reason);
      },
    });
  });
}

async function authenticateVBCUserWithSSOPopup(): Promise<void> {
  const forceLogin = localStorage.getItem(SSO_FORCE_LOGIN_KEY) === 'true' ? '?forceLogin=true' : '';
  const tokens = await openTeamsAuthenticationPopup(`${window.location.origin}${MS_TEAMS_AUTH_STEPS.START}${forceLogin}`);

  setTokens(tokens);
}

async function logoutUserWithPopup(): Promise<void> {
  const { context } = useContext();
  const { destroyServices } = useServices();
  const appId = context.value.appId;
  const sessionId = state.session.id;

  try {
    try {
      await destroyServices();
    } catch (e) {
      console.error('Failed to clean up services during logout', e);
    }
    await openTeamsAuthenticationPopup(`${window.location.origin}${MS_TEAMS_AUTH_STEPS.LOGOUT}?sessionId=${encodeURIComponent(sessionId)}`);
  } catch (e) {
    console.error('Provider logout did not complete; clearing the local session', e);
  } finally {
    // The popup may not share the Teams iframe's storage partition.
    clearTokens();
    localStorage.removeItem(TEAMS_STATE_ID_KEY);
    state.session.id = '';
  }

  // We have to retain the entitiyId after logout.
  // This is a workaround for a Microsoft bug in the mobile implementation that does not pass the current entitiyId when calling the getContext function from the SDK.
  window.location.replace(`${window.location.origin}${MS_TEAMS_AUTH_STEPS.BASE}?entityId=${encodeURIComponent(appId)}`);
}

async function getMSTeamsUserAuthToken(): Promise<string> {
  return new Promise((resolve, reject) => {
    const authTokenRequest = {
      successCallback(token) {
        resolve(token);
      },
      failureCallback(error) {
        reject(error);
      },
      silent: true,
    };
    MSTeamsSDK.authentication.getAuthToken(authTokenRequest);
  });
}

async function startTeamsVBCSession() {
  let token;
  let session;
  let tenantId;

  if (!activeExtension.value) {
    throw new Error('Cannot init Teams session without an active VBC extension.');
  }

  if (state.session && state.session.id) {
    return;
  }

  try {
    tenantId = await new Promise((resolve) => {
      MSTeamsSDK.getContext((context) => {
        resolve(context.tid);
      });
    });
  } catch (e) {
    console.error(e);
    throw new SessionCreationError();
  }

  try {
    token = await getMSTeamsUserAuthToken();
  } catch (e) {
    console.error(e);
    throw new AdminConsentError(tenantId);
  }

  try {
    console.log('Calling Teams Session API');
    session = await createSession(token, activeExtension.value.id, activeExtension.value.extension);
    console.log(JSON.stringify(session));
    state.session = session;
    localStorage.setItem(TEAMS_STATE_ID_KEY, JSON.stringify(toRaw(state)));
  } catch (e) {
    console.error(e);
    console.log(JSON.stringify(e));
    if (e.response && e.response.status === 403 && e.response.data === 'admin_consent_required') {
      throw new AdminConsentError(tenantId);
    }
    throw new SessionCreationError();
  }
}

async function endTeamsVBCSession(sessionId) {
  try {
    if (sessionId) {
      console.log(`Calling endSession endpoint with sessionId ${sessionId}`);
      await endSession(sessionId);
    }
  } catch (e) {
    MSTeamsSDK.authentication.notifyFailure(FAILED_TO_REMOVE_SESSION_LOGOUT_ERROR_REASON);
    return;
  }

  try {
    await logout({ postLogoutURL: `${window.location.origin}${MS_TEAMS_AUTH_STEPS.POST_LOGOUT}` });
  } catch (e) {
    MSTeamsSDK.authentication.notifyFailure(e.msg);
  }
}

function notifyTeamsLogout() {
  MSTeamsSDK.initialize(() => {
    MSTeamsSDK.authentication.notifySuccess(JSON.stringify({ reason: 'Logout' }));
  });
}

function redirectToVBCSSO() {
  redirectToSSO({
    authCallbackURL: `${window.location.origin}${MS_TEAMS_AUTH_STEPS.CALLBACK}`,
    forceLogin: getQueryParameters().forceLogin === 'true',
  });
}

function handleUserConsentResponse() {
  // const fragmentParameters = getFragmentParameters();
  //
  // if (fragmentParameters.error) {
  //   MSTeamsSDK.authentication.notifyFailure(fragmentParameters.error);
  //   return;
  // }

  redirectToVBCSSO();
}

async function consumeVBCSSOTokens() {
  try {
    const { VBCTokens } = await consumeSSOTokens({ authCallbackURL: `${window.location.origin}${MS_TEAMS_AUTH_STEPS.CALLBACK}` });
    // Resolve authentication back into the original tab with the user's tokens.
    MSTeamsSDK.authentication.notifySuccess(JSON.stringify(VBCTokens));
  } catch (e) {
    console.error(e);
    MSTeamsSDK.authentication.notifyFailure(e.msg);
  }
}

function ready() {
  MSTeamsSDK.appInitialization.notifySuccess();
}

export default function useMSTeams() {
  return {
    MSTeamsSDK,
    parseTeamsContext,
    ready,
    consumeVBCSSOTokens,
    redirectToUserConsentPage,
    authenticateVBCUserWithSSOPopup,
    handleUserConsentResponse,
    startTeamsVBCSession,
    endTeamsVBCSession,
    logoutUserWithPopup,
    notifyTeamsLogout,
  };
}
